// @@CARRIER_BILLS_v119@@ Shipping › Carrier Bills — import carrier invoice data (FedEx Billing Online CSV;
// @@CARRIER_BILLS_UPS_v120@@ UPS Billing Data CSV), assign each shipment to a client (billable or not), then
// create one Zoho Books bill per carrier invoice. The FedEx/UPS switch scopes settings, import and the list.
// admin/manager only. Lines are pre-matched server-side (HQ label by tracking #, then the client code
// in the reference); every change here saves immediately and is never re-guessed on re-import.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

function money(n, ccy) {
  if (n == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(n); }
  catch (e) { return '$' + Number(n).toFixed(2); }
}
// YYYY-MM-DD read by string slice — never new Date(iso) (Phoenix shows the day before).
function day(s) {
  if (!s) return '—';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y) return '—';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}
function stamp(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (e) { return ''; }
}
async function jsonOf(r) { return r.json().catch(() => ({})); }
const errText = (r, j, fallback) => (j && j.message) || (r.status === 503 && j && j.code === 'migration_missing' ? 'The v119 database migration has not been applied yet.' : (j && j.error) || fallback);

const MATCH = {
  label: ['HQ label', 'Matched by tracking # to a label made in HQ'],
  reference: ['Reference', 'Matched by the client code or client name in the reference'],
  manual: ['Edited', 'Set by hand — kept on re-import'],
};
const FILTERS = [
  { k: 'all', label: 'All' },
  { k: 'none', label: 'No client' },
  { k: 'billable', label: 'Billable' },
  { k: 'expense', label: 'XYZ expense' },
];
const lineMatches = (l, f) => (f === 'all' ? true : f === 'none' ? !l.client_id : f === 'billable' ? l.billable : !l.billable);
const CARRIER = {
  fedex: { label: 'FedEx', help: <>FedEx Billing Online › Invoices › Reporting › Create report → <em>All columns</em> → CSV. One file can hold many invoices.</> },
  ups: { label: 'UPS', help: <>UPS Billing Center › My Invoices › open an invoice › View / Download Invoice Data → <em>UPS Billing Data (.csv)</em>. Not the My Invoices list.</> },
};
const CARRIER_KEY = 'hq.carrierbills.carrier';
// @@CARRIER_BILLS_MARKUP_v121@@ client markup on billable lines (server owns the math; these are for display/inputs)
const DEFAULT_MARKUP = 10;
const pct = (n) => (n == null ? '—' : (Math.round(Number(n) * 100) / 100) + '%');
const MARKUP_NOTE = {
  not_applied: 'Zoho created the bill but did not keep the markup.',
  rejected: 'Zoho refused the markup, so the bill was created without it.',
  unverified: 'Zoho did not confirm the markup (or this bill already existed).',
};
const markupOk = (v) => v === '' || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100);
const clientLabel = (c) => (c.name || 'Unnamed') + (c.designation_code ? ' (' + c.designation_code + ')' : '') + (c.zoho_linked ? '' : ' · not in Zoho');

// @@CARRIER_BILLS_MARKUP_v121@@ client price of a billable line (display; the server computes the same)
const billTo = (l, def) => Math.round(Number(l.net_charge) * (1 + (l.markup_pct != null ? Number(l.markup_pct) : def) / 100) * 100) / 100;

// Per-line markup: blank = follow the carrier default (shown as the placeholder). Saves on blur / Enter.
function MarkupInput({ value, def, disabled, onCommit }) {
  const shown = value == null ? '' : String(value);
  const [v, setV] = useState(shown);
  useEffect(() => { setV(shown); }, [shown]);
  const commit = () => {
    const t = String(v).trim();
    if (t === shown) return;
    if (!markupOk(t)) { setV(shown); return; }
    onCommit(t);
  };
  return (
    <span className="hq-v121-mkwrap">
      <input className="hq-input hq-v121-mk" type="number" min="0" max="100" step="0.01" inputMode="decimal" aria-label="Markup percent"
        placeholder={String(def)} value={v} disabled={disabled} onChange={(e) => setV(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setV(shown); } }} />%
    </span>
  );
}

function ClientSelect({ value, clients, onChange, disabled, id }) {
  return (
    <select id={id} className="hq-select hq-v119-client" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">— XYZ expense —</option>
      {clients.map((c) => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
    </select>
  );
}

// ── Zoho settings (vendor + expense account) ─────────────────────────────────
function SettingsCard({ carrier, settings, onSaved }) {
  const missing = !settings || !settings.zoho_vendor_id || !settings.zoho_account_id;
  const [open, setOpen] = useState(missing);
  const [opts, setOpts] = useState(null);
  const [vendor, setVendor] = useState('');
  const [account, setAccount] = useState('');
  const [markup, setMarkup] = useState(() => String(settings && settings.markup_pct != null ? settings.markup_pct : DEFAULT_MARKUP)); // @@CARRIER_BILLS_MARKUP_v121@@
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (missing) setOpen(true); }, [missing]);
  useEffect(() => {
    if (!open || opts) return undefined;
    let on = true;
    apiFetch('/api/carrier-bills/zoho-options').then(async (r) => {
      const j = await jsonOf(r);
      if (!on) return;
      if (!r.ok) { setErr(errText(r, j, 'Could not load Zoho options.')); return; }
      setOpts(j);
      setVendor((settings && settings.zoho_vendor_id) || (j.suggest && j.suggest[carrier + '_vendor']) || '');
      setAccount((settings && settings.zoho_account_id) || (j.suggest && j.suggest.account) || '');
    }).catch(() => { if (on) setErr('Could not reach the server.'); });
    return () => { on = false; };
  }, [open, opts, settings, carrier]);
  const groups = useMemo(() => {
    const g = new Map();
    for (const a of (opts && opts.accounts) || []) { if (!g.has(a.account_type)) g.set(a.account_type, []); g.get(a.account_type).push(a); }
    return [...g.entries()];
  }, [opts]);
  async function save(e) {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const r = await apiFetch('/api/carrier-bills/settings/' + carrier, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zoho_vendor_id: vendor, zoho_account_id: account, markup_pct: markup === '' ? null : Number(markup) }),
      });
      const j = await jsonOf(r);
      if (!r.ok) { setErr(errText(r, j, 'Could not save.')); return; }
      onSaved(j.settings); setOpen(false);
    } catch (e2) { setErr('Could not reach the server.'); }
    finally { setBusy(false); }
  }
  return (
    <div className={'hq-v119-settings' + (missing ? ' is-missing' : '')}>
      <div className="hq-v119-settings-row">
        <div>
          <span className="hq-v119-k">{CARRIER[carrier].label} bills post to</span>
          {missing ? <span className="hq-v119-warn-t">Not set — pick the Zoho vendor and expense account before creating bills.</span>
            : <span>{settings.zoho_vendor_name} · {settings.zoho_account_name} · <strong>{pct(settings.markup_pct)}</strong> client markup</span>}
        </div>
        {!open && <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setOpen(true)}>Change</button>}
      </div>
      {open && (
        <form className="hq-v119-settings-form" onSubmit={save}>
          {!opts && !err && <p className="hq-v119-muted">Loading Zoho vendors and accounts…</p>}
          {opts && (
            <>
              <label>
                <span className="hq-v119-k">Zoho vendor</span>
                <select className="hq-select" value={vendor} onChange={(e) => setVendor(e.target.value)} required>
                  <option value="">Pick a vendor…</option>
                  {opts.vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                {!opts.vendors.length && <span className="hq-v119-muted hq-v119-small">No Zoho vendors cached — refresh on the <Link to="/vendors">Vendors</Link> page.</span>}
              </label>
              <label>
                <span className="hq-v119-k">Expense account</span>
                {opts.accounts ? (
                  <select className="hq-select" value={account} onChange={(e) => setAccount(e.target.value)} required>
                    <option value="">Pick an account…</option>
                    {groups.map(([type, list]) => (
                      <optgroup key={type} label={type.replace(/_/g, ' ')}>
                        {list.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}{a.account_code ? ' · ' + a.account_code : ''}</option>)}
                      </optgroup>
                    ))}
                  </select>
                ) : <span className="hq-v119-err">Could not reach Zoho for the chart of accounts.</span>}
              </label>
              <label className="hq-v121-mk-field">
                <span className="hq-v119-k">Client markup %</span>
                <input className="hq-input" type="number" min="0" max="100" step="0.01" inputMode="decimal" value={markup}
                  onChange={(e) => setMarkup(e.target.value)} placeholder={String(DEFAULT_MARKUP)} />
                <span className="hq-v119-muted hq-v119-small">Default for billable lines</span>
              </label>
              <div className="hq-v119-settings-acts">
                {!missing && <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>}
                <button type="submit" className="hq-btn hq-v119-go" disabled={busy || !vendor || !account || !markupOk(markup)}>{busy ? 'Saving…' : 'Save'}</button>
              </div>
            </>
          )}
          {err && <p className="hq-v119-err">{err}</p>}
        </form>
      )}
    </div>
  );
}

// ── import drop zone ─────────────────────────────────────────────────────────
function ImportCard({ carrier, onImported }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const input = useRef(null);
  async function send(file) {
    if (!file) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const text = await file.text();
      const r = await apiFetch('/api/carrier-bills/import?carrier=' + carrier, {
        method: 'POST', headers: { 'Content-Type': 'text/plain', 'X-Filename': encodeURIComponent(file.name) }, body: text,
      });
      const j = await jsonOf(r);
      if (!r.ok) { setErr(errText(r, j, 'Import failed.')); return; }
      setResult(j); onImported(j);
    } catch (e) { setErr('Could not read or upload that file.'); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  }
  return (
    <div className="hq-v119-import">
      <div className={'hq-v119-drop' + (drag ? ' is-drag' : '') + (busy ? ' is-busy' : '')}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files && e.dataTransfer.files[0]); }}
        onClick={() => !busy && input.current && input.current.click()} role="button" tabIndex={0}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && input.current) input.current.click(); }}>
        <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={(e) => send(e.target.files && e.target.files[0])} />
        <strong>{busy ? 'Importing…' : 'Drop a ' + CARRIER[carrier].label + ' invoice CSV here, or click to choose'}</strong>
        <span>{CARRIER[carrier].help}</span>
      </div>
      {err && <p className="hq-v119-err">{err}</p>}
      {result && (
        <div className="hq-v119-result">
          {result.imported.map((x) => (
            <div key={x.id}>
              <strong>{x.invoice_number}</strong> · {x.line_count} line{x.line_count === 1 ? '' : 's'} · {money(x.total, x.currency_code)} ·{' '}
              <span className={'hq-v119-imp hq-v119-imp-' + x.import_status}>{x.import_status === 'new' ? 'New' : x.import_status === 'updated' ? 'Updated (your edits kept)' : 'Already in Zoho — not changed'}</span>
              {x.import_status !== 'already_pushed' && <> · {x.assigned_count}/{x.line_count} matched to a client</>}
            </div>
          ))}
          {result.skipped > 0 && <div className="hq-v119-muted">{result.skipped} row{result.skipped === 1 ? '' : 's'} skipped (no invoice number or charge).</div>}
          {(result.warnings || []).map((w, i) => <div key={i} className="hq-v119-warn-t">{w}</div>)}
        </div>
      )}
    </div>
  );
}

// ── one invoice: review + push ───────────────────────────────────────────────
function InvoiceDetail({ id, clients, settings, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [bulkClient, setBulkClient] = useState('');
  const [bulkBill, setBulkBill] = useState(true);
  const [bulkMarkup, setBulkMarkup] = useState('');
  const [confirm, setConfirm] = useState(null); // 'push' | 'reopen' | 'delete'

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/carrier-bills/' + id);
      const j = await jsonOf(r);
      if (r.ok) { setD(j); setErr(''); } else setErr(errText(r, j, 'Could not load this invoice.'));
    } catch (e) { setErr('Could not reach the server.'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function save(lines) {
    setSaving(true); setErr('');
    // optimistic: show the change at once; the server response (or a reload on error) replaces it
    const by = new Map(lines.map((x) => [x.id, x]));
    const nameOf = (cid) => { const c = clients.find((k) => k.id === cid); return c || null; };
    setD((cur) => (cur ? { ...cur, lines: cur.lines.map((l) => {
      const e = by.get(l.id); if (!e) return l;
      let n = l;
      if ('client_id' in e) {
        const c = nameOf(e.client_id);
        n = { ...n, client_id: e.client_id, billable: !!(e.client_id && e.billable), match_source: 'manual',
          client_name: c ? c.name : null, designation_code: c ? c.designation_code : null, client_in_zoho: c ? !!c.zoho_linked : false };
      }
      if ('markup_pct' in e) n = { ...n, markup_pct: e.markup_pct };
      return n;
    }) } : cur));
    try {
      const r = await apiFetch('/api/carrier-bills/' + id + '/lines', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lines }),
      });
      const j = await jsonOf(r);
      if (!r.ok) { setErr(errText(r, j, 'Could not save.')); load(); return false; }
      setD(j); onChanged(j.invoice); return true;
    } catch (e) { setErr('Could not reach the server.'); load(); return false; }
    finally { setSaving(false); }
  }
  const setClient = (l, cid) => save([{ id: l.id, client_id: cid, billable: cid ? (l.client_id ? l.billable : true) : false }]);
  const setBillable = (l, b) => save([{ id: l.id, client_id: l.client_id, billable: b }]);
  const setMarkupFor = (ids, v) => save(ids.map((lid) => ({ id: lid, markup_pct: v === '' || v == null ? null : Number(v) })));
  async function applyBulkMarkup() {
    if (!markupOk(bulkMarkup)) return;
    if (await setMarkupFor([...sel], bulkMarkup)) setSel(new Set());
  }
  async function applyBulk() {
    const lines = [...sel].map((lid) => ({ id: lid, client_id: bulkClient || null, billable: !!(bulkClient && bulkBill) }));
    if (await save(lines)) setSel(new Set());
  }

  async function act(kind) {
    setSaving(true); setErr('');
    try {
      const url = '/api/carrier-bills/' + id + (kind === 'push' ? '/push' : kind === 'reopen' ? '/reopen' : '');
      const r = await apiFetch(url, { method: kind === 'delete' ? 'DELETE' : 'POST' });
      const j = await jsonOf(r);
      setConfirm(null);
      if (!r.ok) { setErr(errText(r, j, 'That did not work.')); if (j.invoice) onChanged(j.invoice); return; }
      if (kind === 'delete') { onChanged(null, id); return; }
      setD(j);
      const note = kind === 'push'
        ? (j.reused ? 'Bill ' + j.invoice.zoho_bill_number + ' was already in Zoho — linked it instead of creating a duplicate.'
          : 'Bill ' + j.invoice.zoho_bill_number + ' created in Zoho.' + (j.linked === false ? ' (HQ could not record it — check the server log.)' : '')
            + (MARKUP_NOTE[j.markup] ? ' ' + MARKUP_NOTE[j.markup] + ' Set “Mark up by” on the billable lines in Zoho.' : ''))
        : 'Invoice ' + j.invoice.invoice_number + ' reopened — it can be edited and sent again.';
      onChanged(j.invoice, null, { text: note, url: kind === 'push' ? j.invoice.zoho_url : null });
    } catch (e) { setErr('Could not reach the server.'); }
    finally { setSaving(false); }
  }

  const lines = useMemo(() => (d ? d.lines : []), [d]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.k, lines.filter((l) => lineMatches(l, f.k)).length])), [lines]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return lines.filter((l) => lineMatches(l, filter)).filter((l) => !s
      || [l.tracking_number, l.reference, l.po_number, l.recipient, l.destination, l.client_name, l.service].some((v) => v && String(v).toLowerCase().includes(s)));
  }, [lines, filter, q]);

  if (err && !d) return <div className="hq-v119-detail"><p className="hq-v119-err">{err}</p></div>;
  if (!d) return <div className="hq-v119-detail"><p className="hq-v119-muted">Loading shipments…</p></div>;
  const inv = d.invoice;
  const pending = inv.status === 'pending';
  const defMk = d.summary.default_markup_pct != null ? d.summary.default_markup_pct : DEFAULT_MARKUP;
  const ccy = inv.currency_code;
  const allShown = shown.length > 0 && shown.every((l) => sel.has(l.id));
  const unlinked = d.summary.clients.filter((c) => !c.client_in_zoho);
  const noSettings = !settings || !settings.zoho_vendor_id || !settings.zoho_account_id;
  const mismatch = inv.amount_due != null && Math.abs(inv.amount_due - inv.total) > 0.01;

  return (
    <div className="hq-v119-detail">
      <div className="hq-v119-toolbar">
        <div className="hq-v101-seg hq-v119-seg">
          {FILTERS.map((f) => <button key={f.k} type="button" className={filter === f.k ? 'on' : ''} onClick={() => setFilter(f.k)}>{f.label} ({counts[f.k]})</button>)}
        </div>
        <input className="hq-input hq-v119-search" type="search" placeholder="Search tracking #, reference, recipient…" value={q} onChange={(e) => setQ(e.target.value)} />
        {saving && <span className="hq-v119-muted hq-v119-small">Saving…</span>}
      </div>
      {pending && sel.size > 0 && (
        <div className="hq-v119-bulk">
          <span>{sel.size} selected</span>
          <ClientSelect value={bulkClient} clients={clients} onChange={(v) => setBulkClient(v || '')} disabled={saving} />
          <label className="hq-v119-chk"><input type="checkbox" checked={!!bulkClient && bulkBill} disabled={!bulkClient} onChange={(e) => setBulkBill(e.target.checked)} /> Billable</label>
          <button type="button" className="hq-btn hq-v119-go" onClick={applyBulk} disabled={saving}>Apply</button>
          <span className="hq-v121-bulk-mk">
            <input className="hq-input hq-v121-mk" type="number" min="0" max="100" step="0.01" inputMode="decimal" aria-label="Markup percent for selected"
              placeholder={String(d.summary.default_markup_pct != null ? d.summary.default_markup_pct : DEFAULT_MARKUP)} value={bulkMarkup} onChange={(e) => setBulkMarkup(e.target.value)} />%
            <button type="button" className="hq-btn hq-btn-ghost" onClick={applyBulkMarkup} disabled={saving || !markupOk(bulkMarkup)} title="Blank = back to the default">Set markup</button>
          </span>
          <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setSel(new Set())} disabled={saving}>Clear</button>
        </div>
      )}
      <div className="hq-v119-scroll">
        <table className="hq-v119-lines">
          <thead>
            <tr>
              {pending && <th className="hq-v119-cb"><input type="checkbox" aria-label="Select all shown" checked={allShown}
                onChange={(e) => setSel((s) => { const n = new Set(s); shown.forEach((l) => (e.target.checked ? n.add(l.id) : n.delete(l.id))); return n; })} /></th>}
              <th>Tracking #</th><th className="hq-v119-hide-sm">Shipped</th><th className="hq-v119-hide-md">Service</th><th>Reference</th>
              <th className="hq-v119-hide-md">To</th><th className="r">Charge</th><th>Client</th><th className="hq-v121-billhead">Billable<span>markup</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => (
              <tr key={l.id} className={l.billable ? 'is-billable' : ''}>
                {pending && <td className="hq-v119-cb"><input type="checkbox" aria-label={'Select ' + (l.tracking_number || 'line ' + l.line_no)} checked={sel.has(l.id)}
                  onChange={(e) => setSel((s) => { const n = new Set(s); if (e.target.checked) n.add(l.id); else n.delete(l.id); return n; })} /></td>}
                <td className="hq-v119-trk">
                  {l.package_id ? <Link to={'/packages/' + l.package_id} title={l.package_label || 'HQ package'}>{l.tracking_number}</Link> : (l.tracking_number || <span className="hq-v119-muted">Invoice charge</span>)}
                  {l.master_tracking && <div className="hq-v119-muted hq-v119-small">MPS {l.master_tracking}</div>}
                  {l.pieces > 1 && <div className="hq-v119-muted hq-v119-small">{l.pieces} packages</div>}
                  {MATCH[l.match_source] && <span className="hq-v119-chip" title={MATCH[l.match_source][1]}>{MATCH[l.match_source][0]}</span>}
                </td>
                <td className="hq-v119-hide-sm hq-v119-nowrap">{day(l.ship_date)}</td>
                <td className="hq-v119-hide-md">{l.service || '—'}</td>
                <td className="hq-v119-ref">{l.reference || <span className="hq-v119-muted">—</span>}{l.po_number && <div className="hq-v119-muted hq-v119-small">PO {l.po_number}</div>}</td>
                <td className="hq-v119-hide-md hq-v120-to">{l.recipient || '—'}{l.destination && <div className="hq-v119-muted hq-v119-small">{l.destination}</div>}</td>
                <td className="r hq-v119-amt" title={l.charges.map((c) => c.description + ': ' + money(c.amount, ccy)).join('\n')}>
                  {money(l.net_charge, ccy)}
                  {l.charges.length > 1 && <div className="hq-v119-muted hq-v119-small">{l.charges.length} charges</div>}
                  {l.billable && <div className="hq-v121-billto" title="What the client is invoiced: cost + markup">→ {money(billTo(l, defMk), ccy)}</div>}
                </td>
                <td>{pending ? <ClientSelect value={l.client_id} clients={clients} onChange={(v) => setClient(l, v)} disabled={saving} />
                  : (l.client_name ? l.client_name + (l.designation_code ? ' (' + l.designation_code + ')' : '') : <span className="hq-v119-muted">XYZ expense</span>)}
                  {pending && l.billable && !l.client_in_zoho && <div className="hq-v119-warn-t hq-v119-small">Client not linked to Zoho</div>}
                </td>
                <td className="hq-v119-billcol">
                  <input type="checkbox" aria-label="Billable" checked={l.billable} disabled={!pending || saving || !l.client_id} onChange={(e) => setBillable(l, e.target.checked)} />
                  {l.billable && (pending
                    ? <MarkupInput value={l.markup_pct} def={defMk} disabled={saving} onCommit={(v) => setMarkupFor([l.id], v)} />
                    : <div className="hq-v121-mkro">{pct(l.markup_pct != null ? l.markup_pct : defMk)}</div>)}
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={pending ? 9 : 8} className="hq-v119-muted">No lines match.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="hq-v119-foot">
        <div className="hq-v119-sumtbl">
          <div className="hq-v119-k">Billable to clients</div>
          {d.summary.clients.length === 0 && <div className="hq-v119-muted">Nothing billable on this invoice.</div>}
          {d.summary.clients.length > 0 && (
            <div className="hq-v121-sumrow hq-v121-sumhead"><span /><span className="r">Cost</span><span className="r">Markup</span><span className="r">Client pays</span></div>
          )}
          {d.summary.clients.map((c) => (
            <div key={c.client_id} className="hq-v121-sumrow">
              <span>{c.client_name || 'Client'}{c.designation_code ? ' (' + c.designation_code + ')' : ''}{!c.client_in_zoho && <span className="hq-v119-warn-t"> · not linked to Zoho</span>}
                <span className="hq-v119-muted hq-v119-small"> · {c.count} shipment{c.count === 1 ? '' : 's'}</span></span>
              <span className="r">{money(c.total, ccy)}</span>
              <span className="r hq-v119-muted">+{money(c.markup_total, ccy)}</span>
              <span className="r hq-v121-pays">{money(c.bill_to, ccy)}</span>
            </div>
          ))}
          <div className="hq-v119-sumrow"><span>XYZ shipping expense</span><span /><span className="r">{money(d.summary.non_billable_total, ccy)}</span></div>
          <div className="hq-v119-sumrow is-total"><span>Bill total</span><span className="hq-v119-muted">{inv.line_count} line{inv.line_count === 1 ? '' : 's'}</span><span className="r">{money(inv.total, ccy)}</span></div>
          <p className="hq-v119-muted hq-v119-small hq-v121-note">
            Bill total is what you owe {inv.carrier_label}. Billable lines carry their markup in Zoho ("Mark up by"), so adding a client's unbilled bills to an invoice charges cost + markup.
          </p>
          {mismatch && <div className="hq-v119-warn-t hq-v119-small">{inv.carrier_label} shows {money(inv.amount_due, ccy)} on this invoice — the file may only hold part of it.</div>}
        </div>
        <div className="hq-v119-acts">
          {pending ? (
            <>
              <p className="hq-v119-muted hq-v119-small">
                {noSettings ? 'Set the Zoho vendor and expense account above first.' : 'Creates bill ' + inv.invoice_number + ' for ' + settings.zoho_vendor_name + ' on ' + settings.zoho_account_name + '. Billable lines go to each client\'s unbilled expenses in Zoho.'}
              </p>
              {unlinked.length > 0 && <p className="hq-v119-warn-t hq-v119-small">Link {unlinked.map((c) => c.client_name).join(', ')} to a Zoho customer (client page) or untick their lines.</p>}
              {confirm === 'push' ? (
                <div className="hq-v119-confirm">
                  <span>Create the bill in Zoho now?</span>
                  <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setConfirm(null)} disabled={saving}>Cancel</button>
                  <button type="button" className="hq-btn hq-v119-go" onClick={() => act('push')} disabled={saving}>{saving ? 'Creating…' : 'Yes, create bill'}</button>
                </div>
              ) : confirm === 'delete' ? (
                <div className="hq-v119-confirm">
                  <span>Discard this import? (Nothing in Zoho changes.)</span>
                  <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setConfirm(null)} disabled={saving}>Cancel</button>
                  <button type="button" className="hq-btn hq-v119-danger" onClick={() => act('delete')} disabled={saving}>Discard</button>
                </div>
              ) : (
                <div className="hq-v119-confirm">
                  <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setConfirm('delete')} disabled={saving}>Discard import</button>
                  <button type="button" className="hq-btn hq-v119-go" onClick={() => setConfirm('push')} disabled={saving || noSettings || unlinked.length > 0 || inv.total <= 0}>Create bill in Zoho</button>
                </div>
              )}
              {inv.total <= 0 && <p className="hq-v119-warn-t hq-v119-small">This invoice nets to a credit — enter it in Zoho as a vendor credit.</p>}
            </>
          ) : (
            <>
              <p><span className="hq-v119-k">In Zoho</span>Bill {inv.zoho_bill_number}{inv.zoho_bill_total != null ? ' · ' + money(inv.zoho_bill_total, ccy) : ''}</p>
              <p className="hq-v119-muted hq-v119-small">{inv.pushed_by ? 'By ' + inv.pushed_by + ' · ' : ''}{stamp(inv.pushed_at)}</p>
              {inv.markup_status === 'applied' && <p className="hq-v121-ok hq-v119-small">Markup set on the billable lines in Zoho ✓</p>}
              {['not_applied', 'rejected', 'unverified'].includes(inv.markup_status) && (
                <p className="hq-v119-warn-t hq-v119-small">{MARKUP_NOTE[inv.markup_status]} Open the bill in Zoho and set “Mark up by” on the billable lines before invoicing the client.</p>
              )}
              <div className="hq-v119-confirm">
                {inv.zoho_url && <a className="hq-btn hq-btn-ghost" href={inv.zoho_url} target="_blank" rel="noreferrer">Open in Zoho</a>}
                {confirm === 'reopen' ? (
                  <>
                    <span className="hq-v119-small">Only if the bill was deleted in Zoho. Reopen?</span>
                    <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setConfirm(null)} disabled={saving}>No</button>
                    <button type="button" className="hq-btn hq-v119-danger" onClick={() => act('reopen')} disabled={saving}>Reopen</button>
                  </>
                ) : <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setConfirm('reopen')}>Reopen…</button>}
              </div>
            </>
          )}
          {err && <p className="hq-v119-err">{err}</p>}
        </div>
      </div>
    </div>
  );
}

export default function CarrierBills() {
  const [carrier, setCarrierState] = useState(() => { try { return localStorage.getItem(CARRIER_KEY) === 'ups' ? 'ups' : 'fedex'; } catch (e) { return 'fedex'; } });
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState(null);
  const [allSettings, setAllSettings] = useState(undefined);
  const settings = allSettings === undefined ? undefined : (allSettings[carrier] || null);
  const setSettings = (s) => setAllSettings((a) => ({ ...(a || {}), [carrier]: s }));
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [ver, setVer] = useState(0); // bumps on import so an open invoice reloads its lines
  const [notice, setNotice] = useState(null);

  function setCarrier(c) {
    if (c === carrier) return;
    try { localStorage.setItem(CARRIER_KEY, c); } catch (e) { /* private mode */ }
    setNotice(null); setOpen(null); setTab('pending'); setCarrierState(c);
  }
  const load = useCallback(async (status) => {
    try {
      const r = await apiFetch('/api/carrier-bills?status=' + status + '&carrier=' + carrier);
      const j = await jsonOf(r);
      if (r.ok) { setData(j); setLoadErr(''); } else { setData({ invoices: [], counts: {} }); setLoadErr(errText(r, j, 'Could not load invoices.')); }
    } catch (e) { setLoadErr('Could not reach the server.'); }
  }, [carrier]);
  useEffect(() => { setData(null); load(tab); }, [tab, load]);
  useEffect(() => {
    apiFetch('/api/carrier-bills/settings').then((r) => (r.ok ? r.json() : null)).then((j) => setAllSettings(j ? j.settings : {})).catch(() => setAllSettings({}));
    apiFetch('/api/carrier-bills/clients').then((r) => (r.ok ? r.json() : null)).then((j) => { if (j) setClients(j.clients); }).catch(() => {});
  }, []);

  function onImported(j) {
    setVer((v) => v + 1);
    if (tab !== 'pending') setTab('pending'); else load('pending');
    const first = (j.imported || []).find((x) => x.import_status !== 'already_pushed');
    if (first) setOpen(first.id);
  }
  function onChanged(inv, deletedId, note) {
    if (note) setNotice(note);
    if (deletedId) { setOpen(null); load(tab); return; }
    if (!inv) return;
    if (inv.status !== tab) { setOpen(inv.id); setTab(inv.status); return; } // follow it to "In Zoho" / "To review"
    setData((d) => (d ? { ...d, invoices: d.invoices.map((x) => (x.id === inv.id ? inv : x)) } : d));
  }

  const invoices = (data && data.invoices) || [];
  const counts = (data && data.counts) || {};
  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Carrier Bills</h1>
        <p>Import FedEx or UPS invoices, check each shipment's client, then create the bill in Zoho Books. Billable lines land in that client's unbilled expenses.</p>
      </div>

      <div className="hq-v101-seg hq-v120-carriers" role="tablist" aria-label="Carrier">
        {Object.entries(CARRIER).map(([k, c]) => {
          const n = data && data.pending_by_carrier ? data.pending_by_carrier[k] : null;
          return <button key={k} type="button" role="tab" aria-selected={carrier === k} className={carrier === k ? 'on' : ''} onClick={() => setCarrier(k)}>{c.label}{n ? ' (' + n + ')' : ''}</button>;
        })}
      </div>

      <div className="hq-panel hq-v119-top">
        {settings !== undefined && <SettingsCard key={'settings-' + carrier} carrier={carrier} settings={settings} onSaved={setSettings} />}
        <ImportCard key={'import-' + carrier} carrier={carrier} onImported={onImported} />
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head">
          <h2>{CARRIER[carrier].label} invoices</h2>
          <div className="hq-v101-seg">
            <button type="button" className={tab === 'pending' ? 'on' : ''} onClick={() => { setNotice(null); setTab('pending'); }}>To review{counts.pending != null ? ' (' + counts.pending + ')' : ''}</button>
            <button type="button" className={tab === 'pushed' ? 'on' : ''} onClick={() => { setNotice(null); setTab('pushed'); }}>In Zoho{counts.pushed != null ? ' (' + counts.pushed + ')' : ''}</button>
          </div>
        </div>
        {notice && (
          <div className="hq-v119-flash hq-v119-flash-page">
            <span>{notice.text}</span>
            {notice.url && <a href={notice.url} target="_blank" rel="noreferrer">Open in Zoho</a>}
            <button type="button" className="hq-modal-x" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
          </div>
        )}
        {loadErr && <p className="hq-v119-err hq-v119-pad">{loadErr}</p>}
        {!data ? <p className="hq-empty">Loading…</p> : (
          <div className="hq-v119-scroll">
            <table className="hq-table hq-v119-table">
              <thead>
                <tr>
                  <th>Invoice #</th><th className="hq-v119-hide-xs">Date</th><th className="hq-v119-hide-sm">Account</th><th className="r hq-v119-hide-xs">Shipments</th>
                  <th className="r hq-v119-hide-sm">Matched</th><th className="r">Billable</th><th className="r">Total</th>
                  <th className="hq-v119-hide-sm">{tab === 'pushed' ? 'Zoho bill' : 'Imported'}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((x) => (
                  <React.Fragment key={x.id}>
                    <tr className={'hq-v119-row' + (open === x.id ? ' is-open' : '')} onClick={() => setOpen(open === x.id ? null : x.id)}>
                      <td className="hq-v119-num"><span className="hq-v119-caret" aria-hidden="true">▸</span>{x.invoice_number}</td>
                      <td className="hq-v119-nowrap hq-v119-hide-xs">{day(x.invoice_date)}</td>
                      <td className="hq-v119-hide-sm">{x.account_number || '—'}</td>
                      <td className="r hq-v119-hide-xs">{x.line_count}</td>
                      <td className="r hq-v119-hide-sm">{x.assigned_count}/{x.line_count}</td>
                      <td className="r hq-v119-amt">{money(x.billable_total, x.currency_code)}</td>
                      <td className="r hq-v119-amt">{money(x.total, x.currency_code)}</td>
                      <td className="hq-v119-hide-sm hq-v119-small">{tab === 'pushed'
                        ? (x.zoho_url ? <a href={x.zoho_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{x.zoho_bill_number}</a> : x.zoho_bill_number)
                        : stamp(x.imported_at) + (x.imported_by ? ' · ' + x.imported_by : '')}</td>
                    </tr>
                    {open === x.id && (
                      <tr className="hq-v119-detailrow"><td colSpan={8}>
                        <InvoiceDetail key={x.id + ':' + ver} id={x.id} clients={clients} settings={settings} onChanged={onChanged} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {invoices.length === 0 && (
                  <tr><td className="hq-empty" colSpan={8}>{tab === 'pending' ? 'Nothing to review — import a ' + CARRIER[carrier].label + ' CSV above.' : 'No bills created from HQ yet.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
